import { Fragment, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { compactIdentifier, getPatientFullName } from '@/api/mappers';
import type { IdentifierDefinitionDto } from '@/api/client';
import { useActiveIdentifierTypes } from '@/hooks/useIdentifierTypes';
import { identifierLabelFor } from '@/lib/identifierLabel';
import { formatPartialDate, getAgeYears } from '@/lib/format';
import type {
  PatientViewModel,
  PersonIdentifierViewModel,
} from '@/models/patient';

/**
 * Maximum identifiers rendered inline before collapsing extras into a
 * "+N more" dropdown. PhenoTips clients can configure unlimited
 * identifiers per patient (External ID + arbitrary labelled IDs); the
 * header has to stay scannable, so anything past this cap moves into
 * the overflow menu.
 */
const HEADER_IDENTIFIER_LIMIT = 3;

/** Matches a year-only partial DOB (`YYYY`), which the header omits in favour of age. */
const YEAR_ONLY = /^\d{4}$/;

interface PatientIdentityBandProps {
  readonly patient: PatientViewModel;
  /** Slot for badges rendered before the action area (e.g. governance level). */
  readonly statusSlot?: ReactNode;
  /** Slot for actions on the right (e.g. Add Task, Export). */
  readonly actionSlot?: ReactNode;
}

/**
 * Patient case header. Three-tier hierarchy: program eyebrow → name +
 * inline status pill → definition-list metadata. Right rail carries
 * tenant slots for governance and global actions.
 */
export function PatientIdentityBand({
  patient,
  statusSlot,
  actionSlot,
}: PatientIdentityBandProps): ReactElement {
  const { t } = useTranslation();
  const { demographics, identifiers } = patient;
  const identifierTypes = useActiveIdentifierTypes().data ?? [];
  const displayName = getPatientFullName(patient) ?? t('patient.header.unknownName');
  const sexLabel = demographics.sex === null ? null : t(`patient.sex.${demographics.sex?.toUpperCase()}`);
  const age = getAgeYears(demographics.dateOfBirth);

  
  const ageSexLabel =
    age !== null && sexLabel !== null
      ? `${t('patient.header.ageYears', { count: age })} · ${sexLabel}`
      : age !== null
        ? t('patient.header.ageYears', { count: age })
        : sexLabel;

  return (
    <header className="bg-card border-b border-border">
      <div className="px-8 py-3 flex items-center gap-6">
        <div className="min-w-0 flex-1">
          <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
            <h1 className="text-[18px] font-semibold leading-none tracking-tight truncate text-foreground">
              {displayName}
            </h1>
            {demographics.lifeStatus === 'ALIVE' && (
              <span className="inline-flex items-center gap-1.5 h-[20px] px-2 rounded-full bg-success/10 text-success text-[10px] font-semibold uppercase tracking-[0.08em]">
                <span
                  aria-hidden="true"
                  className="h-1.5 w-1.5 rounded-full bg-success"
                />
                {t('patient.lifeStatus.ALIVE')}
              </span>
            )}
            {demographics.lifeStatus === 'DECEASED' && (
              <Badge
                variant="destructive"
                className="text-[10px] h-[20px] uppercase tracking-[0.08em] font-semibold rounded-full px-2"
              >
                {t('patient.lifeStatus.DECEASED')}
              </Badge>
            )}
            {patient.metadata.program !== null && (
              <span className="inline-flex items-center h-[20px] px-2 rounded-full bg-muted text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                {patient.metadata.program}
              </span>
            )}
          </div>
          <MetaRow
            ageSexLabel={ageSexLabel}
            dateOfBirth={demographics.dateOfBirth}
            dobLabel={t('patient.header.dateOfBirthLabel')}
            identifiers={identifiers}
            identifierTypes={identifierTypes}
          />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {statusSlot}
          {actionSlot}
        </div>
      </div>
    </header>
  );
}

interface MetaRowProps {
  readonly ageSexLabel: string | null;
  readonly dateOfBirth: string | null;
  readonly dobLabel: string;
  readonly identifiers: PatientViewModel['identifiers'];
  readonly identifierTypes: readonly IdentifierDefinitionDto[];
}

function MetaRow({
  ageSexLabel,
  dateOfBirth,
  dobLabel,
  identifiers,
  identifierTypes,
}: MetaRowProps): ReactElement | null {
  const items: ReactNode[] = [];

  if (ageSexLabel !== null) {
    items.push(
      <span key="age-sex" className="text-foreground/90 font-medium tabular-nums">
        {ageSexLabel}
      </span>,
    );
  }

  // A year-only DOB carries no more information than the computed age, so we
  // suppress the DOB row and let the age chip stand alone rather than render a
  // bare "1964" that reads like a faked date.
  if (dateOfBirth !== null && !YEAR_ONLY.test(dateOfBirth)) {
    items.push(
      <MetaPair
        key="dob"
        label={dobLabel}
        value={
          <span className="tabular-nums">{formatPartialDate(dateOfBirth)}</span>
        }
      />,
    );
  }

  const visibleIds = identifiers.slice(0, HEADER_IDENTIFIER_LIMIT);
  const overflowIds = identifiers.slice(HEADER_IDENTIFIER_LIMIT);

  visibleIds.forEach((identifier, idx) => {
    items.push(
      <IdentifierItem
        key={identifier.id ?? `id-${String(idx)}`}
        identifier={identifier}
        identifierTypes={identifierTypes}
      />,
    );
  });

  if (overflowIds.length > 0) {
    items.push(
      <IdentifierOverflow
        key="id-overflow"
        identifiers={overflowIds}
        identifierTypes={identifierTypes}
      />,
    );
  }

  if (items.length === 0) return null;

  return (
    <dl className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11.5px] text-foreground/80">
      {items.map((item, idx) => (
        <Fragment key={idx}>
          {idx > 0 && (
            <span aria-hidden="true" className="text-border/80 select-none">
              ·
            </span>
          )}
          {item}
        </Fragment>
      ))}
    </dl>
  );
}

interface IdentifierItemProps {
  readonly identifier: PersonIdentifierViewModel;
  readonly identifierTypes: readonly IdentifierDefinitionDto[];
}

function IdentifierItem({ identifier, identifierTypes }: IdentifierItemProps): ReactElement {
  const compact = compactIdentifier(identifier, identifierTypes);
  return (
    <MetaPair
      label={compact.prefix}
      value={<span className="tabular-nums">{compact.value}</span>}
    />
  );
}

interface IdentifierOverflowProps {
  readonly identifiers: readonly PersonIdentifierViewModel[];
  readonly identifierTypes: readonly IdentifierDefinitionDto[];
}

function IdentifierOverflow({ identifiers, identifierTypes }: IdentifierOverflowProps): ReactElement {
  const { t } = useTranslation();
  const count = identifiers.length;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t('patient.header.moreIdentifiersAriaLabel', { count })}
        className="inline-flex items-center h-[22px] px-2 rounded-full border border-border bg-muted text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground hover:bg-card hover:text-foreground transition-colors"
      >
        {t('patient.header.moreIdentifiers', { count })}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={6}
        className="min-w-[220px] py-2"
      >
        <ul className="flex flex-col">
          {identifiers.map((identifier, idx) => {
            const compact = compactIdentifier(identifier, identifierTypes);
            // The dropdown has room for the verbose admin label ("Medical Record No."),
            // unlike the one-line strip which prefers the short form.
            const fullLabel = identifierLabelFor(
              identifierTypes,
              identifier.identifierType,
              identifier.label,
            );
            return (
              <li
                key={identifier.id ?? `overflow-${String(idx)}`}
                className="px-3 py-1.5 flex flex-col gap-0.5"
              >
                {fullLabel.length > 0 && (
                  <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    {fullLabel}
                  </span>
                )}
                <span className="text-[13px] text-foreground tabular-nums break-all">
                  {compact.value}
                </span>
              </li>
            );
          })}
        </ul>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface MetaPairProps {
  readonly label: string | null;
  readonly value: ReactNode;
}

function MetaPair({ label, value }: MetaPairProps): ReactElement {
  return (
    <span className="inline-flex items-baseline gap-1.5">
      {label !== null && (
        <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          {label}
        </dt>
      )}
      <dd className="text-foreground/85">{value}</dd>
    </span>
  );
}
